import BlockUi from '@availity/block-ui';
import { ActiveElement, Chart as ChartJs, ChartEvent, ChartOptions, ChartType, LegendElement, LegendItem, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment/moment';
import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { connect } from 'react-redux';
import { DateInput } from 'semantic-ui-calendar-react-17';
import { Button, Dropdown, Form, Grid, Header, Label, Segment } from 'semantic-ui-react';
import { getFullRouteInfo } from '../../../actions/gtfsStaticActions';
import { getAverageWeekdayStopSeatCapacity, getRidershipFilterCatalog, getRidershipHistory } from '../../../actions/ridershipActions';
import { AppState } from '../../../reducers';
import { getSelectedOrDefaultAgency } from '../../../selectors';
import { FullRouteInfo } from '../../../types/gtfsTypes';
import { PolylineProps, PushpinProps } from '../../../types/MapModels';
import { AverageWeekdayStopSeatCapacityRecord, RidershipFilterCatalogDirection, RidershipStopAggRecord } from '../../../types/ridershipTypes';
import Utils from '../../../utilities/utils';
import type { DayTypeFilter } from '../../../utils/dayType';
import { dayTypeOptions as sharedDayTypeOptions, normalizeDayTypeFilter } from '../../../utils/dayType';
import { getRidershipDateOnlyDaysAgo } from '../../../utils/ridershipDate';
import { amPmToMinutes, minutesToAmPm } from '../../../utils/time';
import { TimeRangeValueType } from '../../runningTimeAnalytics/runningTimesComparison/RunningTimesComparisonForm';
import AzureMapSelectionMap from '../../shared/AzureMap/AzureMapSelectionMap';
import RangeSlider from '../../shared/RangeSlider';
import RidershipExportButton from '../common/RidershipExportButton';
import RidershipMapLegend from '../common/RidershipMapLegend';
import { buildVizFromGtfsAndRows } from '../common/ridershipMapUtils';
import { DirectionSelect, RouteSelect, TripSelect } from '../common/RidershipSelectors';
import { projectAggregateMetrics } from '../common/ridershipUtils';
import StopPopupContent from '../common/StopPopupContent';
import { buildBarColorSeries, buildStopChartLayout, buildStopPopupFromBarSelection, findStopIndexByStopId, getLayerVisibilityByDatasetIndex, getNextActiveBarIndex, RidershipLayerVisibility } from './averageWeekdayByStopChartUtils';
import { ENTIRE_ROUTE_DIRECTION_ID, prependEntireRouteDirectionOption } from './averageWeekdayByStopDirectionUtils';

// A very lightweight mock of ridership data to demonstrate layout and interactions
// When backend API is ready, replace test data fetch with real request

const styles: { [key: string]: React.CSSProperties } = {
    date: {
        display: 'inline-block',
        width: '100%',
    },
    time: {
        display: 'inline-block',
        width: '100%',
        marginRight: '100px',
    },
    navLink: {
        color: '#337ab7',
        float: 'right',
        fontFamily: '"Segoe UI",Verdana,Helvetica,Sans-Serif',
    },
    sliderContainer: {
        marginTop: '40px',
    } as React.CSSProperties,
};

// Default time range should span the whole day ending at 11:59 PM
const TIME_RANGE_DEFAULT: TimeRangeValueType = { start: '12:00 AM', end: '11:59 PM' };
const directionOptionalRouteIds = new Set(['700', '800', '900']);
// Temporary product switch in code (no server config):
// true  - show Heat mode in map mode dropdown;
// false - hide Heat mode and keep only Bubble mode.
const ENABLE_HEAT_MODE = true;

function isAbortError(error: any) {
    return error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';
}

interface Props {
    agency: ReturnType<typeof getSelectedOrDefaultAgency>;
}

interface RouteOption {
    id: string;
    displayName: string;
}

interface DirectionOption {
    id: string;
    displayName: string;
    internalId?: string;
    internalIds: string[];
    trips: { tripId: string; shortTripId?: string; friendlyName?: string; tripHeadsign?: string }[];
}

type RidershipMapMode = 'bubble' | 'heat';
type RidershipHeatMetric = 'boarding' | 'alighting' | 'load';
type RidershipAggregationMode = 'sum' | 'avg' | 'avg_trips';
const defaultLayerVisibility: RidershipLayerVisibility = {
    board: true,
    alight: true,
    load: true,
};

const isDirectionOptional = (route: RouteOption | null) => {
    const routeId = route?.id ?? '';
    return directionOptionalRouteIds.has(routeId);
};

const toShortTripId = (tripId?: string): string => {
    const value = String(tripId ?? '').trim();
    if (!value) return '';
    const parts = value.split('_', 3);
    return parts.length === 3 ? parts[2] : value;
};

const baseChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
        x: {
            stacked: true,
            ticks: {
                maxRotation: 90,
                minRotation: 90,
                autoSkip: false,
                padding: 4,
            },
        },
        y: {
            stacked: false,
            beginAtZero: true,
            title: {
                display: true,
                text: 'Avg Weekday Riders / Seats',
            },
        },
    },
    plugins: {
        legend: { position: 'top' },
        datalabels: { display: false },
        tooltip: {
            mode: 'x' as const,
            intersect: false,
            caretPadding: 110,
            titleFont: { size: 16 },
            bodyFont: { size: 15 },
            callbacks: {
                
                title: function () {
                    return `Activity by Stop`;
                },
                label: function (tooltipItem: TooltipItem<'bar'>) {
                    const dataset = tooltipItem.dataset;
                    const datasetLabel = dataset?.label || 'Value';
                    return ` ${datasetLabel}: ${Utils.roundNumber(tooltipItem.parsed.y as number, 0)}`;
                },
                labelColor: function (tooltipItem: TooltipItem<'bar'>) {
                    const dataset = tooltipItem.dataset as any;
                    const rawBorder = dataset?.borderColor;
                    const rawBackground = dataset?.backgroundColor;
                    const resolveIndexedColor = (colorSource: unknown) => {
                        if (typeof colorSource === 'string') {
                            return colorSource;
                        }
                        if (Array.isArray(colorSource)) {
                            const indexedColor = colorSource[tooltipItem.dataIndex];
                            if (typeof indexedColor === 'string') {
                                return indexedColor;
                            }
                        }
                        return null;
                    };
                    const borderColor = resolveIndexedColor(rawBorder) || '#34495EFF';
                    const backgroundColor = resolveIndexedColor(rawBackground) || borderColor;
                    return {
                        borderColor,
                        backgroundColor,
                    };
                },
            },
        },
        filler: {
            propagate: true,
        },
    },
};

const barSeriesColors = {
    board: {
        activeBackground: 'rgba(46, 204, 113, 0.85)',
        inactiveBackground: 'rgba(46, 204, 113, 0.2)',
        activeBorder: '#27ae60',
        inactiveBorder: 'rgba(39, 174, 96, 0.35)',
    },
    alight: {
        activeBackground: 'rgba(243, 156, 18, 0.85)',
        inactiveBackground: 'rgba(243, 156, 18, 0.2)',
        activeBorder: '#d35400',
        inactiveBorder: 'rgba(211, 84, 0, 0.35)',
    },
    load: {
        activeBackground: 'rgba(52, 152, 219, 0.85)',
        inactiveBackground: 'rgba(52, 152, 219, 0.2)',
        activeBorder: '#2980b9',
        inactiveBorder: 'rgba(41, 128, 185, 0.35)',
    },
} as const;

const RidershipWeekdayByStopPage: React.FC<Props> = (props: Props) => {
    type AppliedFilters = {
        routeId: string;
        directionId: string;
        directionInternalId?: string;
        directionInternalIds?: string[];
        fromDate: string;
        toDate: string;
        dayType: DayTypeFilter;
        tripId?: string;
        tripIds?: string[];
        timeRange: TimeRangeValueType;
    };

    const [routeOptions, setRouteOptions] = React.useState<RouteOption[]>([]);
    const [routesLoading, setRoutesLoading] = React.useState<boolean>(false);
    const [selectedRoute, setSelectedRoute] = React.useState<RouteOption | null>(null);
    const [directionOptions, setDirectionOptions] = React.useState<DirectionOption[]>([]);
    const [directionsLoading, setDirectionsLoading] = React.useState<boolean>(false);
    const [selectedDirection, setSelectedDirection] = React.useState<DirectionOption | null>(null); // directionVariant
    const [directionInternal, setDirectionInternal] = React.useState<string>(''); // directionVariantInternalId
    const [ridershipLoading, setRidershipLoading] = React.useState<boolean>(false);
    const [routeInfo, setRouteInfo] = React.useState<FullRouteInfo | null>(null);
    const [ridRows, setRidRows] = React.useState<RidershipStopAggRecord[] | null>(null);
    const [vehicleCapacityLoading, setVehicleCapacityLoading] = React.useState<boolean>(false);
    const [stopSeatCapacityRows, setStopSeatCapacityRows] = React.useState<AverageWeekdayStopSeatCapacityRecord[]>([]);
    const [stopSeatCapacityLoading, setStopSeatCapacityLoading] = React.useState<boolean>(false);
    const ridershipAbortRef = React.useRef<AbortController | null>(null);
    const stopSeatCapacityAbortRef = React.useRef<AbortController | null>(null);
    const [appliedFilters, setAppliedFilters] = React.useState<AppliedFilters | null>(null);
    const [appliedRouteLabel, setAppliedRouteLabel] = React.useState<string>('');
    const [appliedDirectionLabel, setAppliedDirectionLabel] = React.useState<string>('');
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [hasApplied, setHasApplied] = React.useState<boolean>(false);
    const numberFormatter = React.useMemo(() => new Intl.NumberFormat('en-US'), []);
    const [selectedStopPopup, setSelectedStopPopup] = React.useState<{
        stopId?: string;
        stopName?: string;
        boarding?: number;
        alighting?: number;
        load?: number;
        lat?: number;
        lon?: number;
    } | null>(null);

    const [selectedFeaturesCount, setSelectedFeaturesCount] = useState<number>(0);
    const [selectedStopIds, setSelectedStopIds] = useState<string[]>([]);
    const [activeBarIndex, setActiveBarIndex] = React.useState<number | null>(null);
    const [selectionResetToken, setSelectionResetToken] = useState<number>(0);
    const isHeatModeAvailable = ENABLE_HEAT_MODE;
    const [mapMode, setMapMode] = React.useState<RidershipMapMode>('bubble');
    const [heatMetric, setHeatMetric] = React.useState<RidershipHeatMetric>('load');
    const [layerVisibility, setLayerVisibility] = React.useState<RidershipLayerVisibility>(defaultLayerVisibility);

    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const testPushpins = React.useMemo(() => [
        {
            location: [29.757224, -95.382669] as [number, number],
            options: { title: 'Test Point 1', color: 'red', icon: 'stopIcon' },
            eventHandlers: [],
        },
        {
            location: [29.767224, -95.392669] as [number, number],
            options: { title: 'Test Point 2', color: 'green', icon: 'stopIcon' },
            eventHandlers: [],
        },
    ], []);
    //boardPins.push({ location: [lat, lng], options: { title: label + ' (board)', color: '#2ecc71', icon: 'stopIcon', radius: boardRadius, ...baseOptions }, eventHandlers: [] });

    // Date/day-type filters (inclusive). Defaults: last 30 days, Workdays.
    const defaultTo = getRidershipDateOnlyDaysAgo(0);
    const defaultFrom = getRidershipDateOnlyDaysAgo(30);
    const [fromDate, setFromDate] = React.useState<string>(defaultFrom);
    const [toDate, setToDate] = React.useState<string>(defaultTo);
    const [dayType, setDayType] = React.useState<DayTypeFilter>('workdays');
    const dayTypeOptions = sharedDayTypeOptions;
    const [tripOptions, setTripOptions] = React.useState<{ key: string, value: string, text: string }[]>([]);
    const [tripsLoading, setTripsLoading] = React.useState<boolean>(false);
    const [tripId, setTripId] = React.useState<string>('');
    const [filterCatalogByRouteId, setFilterCatalogByRouteId] = React.useState<Map<string, { routeName: string; directions: RidershipFilterCatalogDirection[] }>>(new Map());
    const [timeRangeState, setTimeRangeState] = useState<TimeRangeValueType>(TIME_RANGE_DEFAULT);
    const [appliedTimeRange, setAppliedTimeRange] = useState<TimeRangeValueType>(TIME_RANGE_DEFAULT);

    const startAbortableRequest = React.useCallback((ref: React.MutableRefObject<AbortController | null>) => {
        ref.current?.abort();
        const controller = new AbortController();
        ref.current = controller;
        return controller;
    }, []);

    const reportLoading = ridershipLoading || vehicleCapacityLoading || stopSeatCapacityLoading;

    const handleCancel = React.useCallback(() => {
        ridershipAbortRef.current?.abort();
        stopSeatCapacityAbortRef.current?.abort();
        setRidershipLoading(false);
        setVehicleCapacityLoading(false);
        setStopSeatCapacityLoading(false);
    }, []);

    // Load routes, directions and trips catalog once per date-range change.
    React.useEffect(() => {
        const agencyId = props.agency?.id;
        if (!agencyId) {
            setRouteOptions([]);
            setSelectedRoute(null);
            setDirectionOptions([]);
            setSelectedDirection(null);
            setTripOptions([]);
            setTripId('');
            setFilterCatalogByRouteId(new Map());
            setAppliedFilters(null);
            setHasApplied(false);
            setRidRows(null);
            setStopSeatCapacityRows([]);
            setAppliedTimeRange(TIME_RANGE_DEFAULT);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setRoutesLoading(true);
                setDirectionsLoading(true);
                setTripsLoading(true);
                const catalog = await getRidershipFilterCatalog(agencyId, fromDate, toDate);
                if (cancelled) return;
                const byRouteId = new Map<string, { routeName: string; directions: RidershipFilterCatalogDirection[] }>();
                (catalog.routes || []).forEach((route) => {
                    byRouteId.set(route.routeId, { routeName: route.routeName, directions: route.directions || [] });
                });
                const options = Array.from(byRouteId.entries()).map(([routeId, route]) => ({
                    id: routeId,
                    displayName: route.routeName || routeId,
                }));
                setFilterCatalogByRouteId(byRouteId);
                setRouteOptions(options);
                setSelectedRoute(prev => {
                    if (prev) {
                        const match = options.find(o => o.id === prev.id);
                        if (match) {
                            return match;
                        }
                    }
                    return options.length > 0 ? options[0] : null;
                });
            } finally {
                if (!cancelled) {
                    setRoutesLoading(false);
                    setDirectionsLoading(false);
                    setTripsLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [props.agency?.id, fromDate, toDate]);

    // Build direction options from loaded catalog for selected route.
    React.useEffect(() => {
        if (!selectedRoute || !filterCatalogByRouteId.size) {
            setDirectionOptions([]);
            setSelectedDirection(null);
            setDirectionInternal('');
            return;
        }
        const routeCatalog = filterCatalogByRouteId.get(selectedRoute.id);
        const catalogDirections = (routeCatalog?.directions || []).map((direction) => ({
            id: direction.directionVariantId,
            displayName: direction.firstStopName
                ? `${direction.directionLabel} (${direction.firstStopName})`
                : direction.directionLabel,
            internalId: direction.directionVariantInternalIds?.[0] || '',
            internalIds: direction.directionVariantInternalIds || [],
            trips: direction.trips || [],
        }));
        const options = prependEntireRouteDirectionOption(catalogDirections);
        setDirectionOptions(options);
        setSelectedDirection(prev => {
            if (prev) {
                const match = options.find(option => option.id === prev.id);
                if (match) {
                    return match;
                }
            }
            return options.length > 0 ? options[0] : null;
        });
        setDirectionsLoading(false);
    }, [selectedRoute, filterCatalogByRouteId]);

    React.useEffect(() => {
        setDirectionInternal(selectedDirection?.internalIds?.[0] || selectedDirection?.internalId || '');
    }, [selectedDirection]);

    // Load full route info for applied route (GTFS shapes and stop order)
    React.useEffect(() => {
        if (!appliedFilters?.routeId) {
            setRouteInfo(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const info = await getFullRouteInfo(appliedFilters.routeId);
                if (!cancelled) setRouteInfo(info);
            } catch {
                if (!cancelled) setRouteInfo(null);
            }
        })();
        return () => { cancelled = true; };
    }, [appliedFilters?.routeId]);

    // Build trips for selected direction from loaded catalog.
    React.useEffect(() => {
        if (!selectedDirection) {
            setTripOptions([]);
            setTripId('');
            setTripsLoading(false);
            return;
        }
        const options = (selectedDirection.trips || []).map((trip) => ({
            key: trip.shortTripId || toShortTripId(trip.tripId) || trip.tripId,
            value: trip.shortTripId || toShortTripId(trip.tripId) || trip.tripId,
            text: trip.friendlyName
                || (trip.tripHeadsign ? `${trip.tripId} (${trip.tripHeadsign})` : trip.tripId),
        }));
        setTripOptions(options);
        setTripId(current => (current && !options.some(option => option.value === current) ? '' : current));
        setTripsLoading(false);
    }, [selectedDirection]);

    const selectedTripFilters = React.useMemo<{ tripId?: string; tripIds?: string[] }>(() => {
        const selectedValue = (tripId || '').trim();
        if (!selectedValue) return {};

        const trip = (selectedDirection?.trips || []).find(item => {
            const fullTripId = String(item.tripId || '').trim();
            const shortId = String(item.shortTripId || toShortTripId(fullTripId)).trim();
            return selectedValue === fullTripId || selectedValue === shortId;
        });

        if (!trip) {
            const fallbackShortId = toShortTripId(selectedValue) || selectedValue;
            return { tripId: fallbackShortId, tripIds: [fallbackShortId] };
        }

        const fullTripId = String(trip.tripId || '').trim();
        const shortId = String(trip.shortTripId || toShortTripId(fullTripId)).trim();
        const tripIds = Array.from(new Set([shortId].filter(value => value.length > 0)));
        return {
            tripId: shortId || fullTripId || undefined,
            tripIds,
        };
    }, [selectedDirection, tripId]);

    const [aggregate, setAggregate] = React.useState<RidershipAggregationMode>('sum');

    // Load ridership history rows for selected route and direction (fetch once; toggle uses preloaded data)
    React.useEffect(() => {
        const agencyId = props.agency?.id;
        if (!agencyId || !appliedFilters) {
            setRidRows(null);
            return;
        }

        let cancelled = false;
        const controller = startAbortableRequest(ridershipAbortRef);
        (async () => {
            try {
                setRidershipLoading(true);
                const records = await getRidershipHistory(
                    agencyId,
                    appliedFilters.routeId,
                    appliedFilters.directionId,
                    appliedFilters.fromDate,
                    appliedFilters.toDate,
                    appliedFilters.dayType,
                    appliedFilters.timeRange.start,
                    appliedFilters.timeRange.end,
                    appliedFilters.tripId || undefined,
                    appliedFilters.directionInternalIds || [],
                    appliedFilters.tripIds || [],
                    { signal: controller.signal });
                if (cancelled) return;
                setRidRows(records || []);
            } catch (error) {
                if (isAbortError(error) || cancelled || controller.signal.aborted) {
                    return;
                }
                throw error;
            } finally {
                if (!cancelled) setRidershipLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [props.agency?.id, appliedFilters, startAbortableRequest]);

    React.useEffect(() => {
        const agencyId = props.agency?.id;
        if (!agencyId || !appliedFilters) {
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                setVehicleCapacityLoading(true);
                if (cancelled) return;
            } finally {
                if (!cancelled) setVehicleCapacityLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [props.agency?.id, appliedFilters]);

    React.useEffect(() => {
        const agencyId = props.agency?.id;
        if (!agencyId || !appliedFilters) {
            setStopSeatCapacityRows([]);
            return;
        }

        let cancelled = false;
        const controller = startAbortableRequest(stopSeatCapacityAbortRef);
        (async () => {
            try {
                setStopSeatCapacityLoading(true);
                const rows = await getAverageWeekdayStopSeatCapacity(
                    agencyId,
                    appliedFilters.routeId,
                    appliedFilters.directionId,
                    appliedFilters.fromDate,
                    appliedFilters.toDate,
                    appliedFilters.dayType,
                    appliedFilters.tripId || undefined,
                    appliedFilters.directionInternalIds || [],
                    appliedFilters.tripIds || [],
                    appliedFilters.timeRange.start,
                    appliedFilters.timeRange.end,
                    { signal: controller.signal });
                if (cancelled) return;
                setStopSeatCapacityRows(rows || []);
            } catch (error) {
                if (isAbortError(error) || cancelled || controller.signal.aborted) {
                    return;
                }
                throw error;
            } finally {
                if (!cancelled) setStopSeatCapacityLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [props.agency?.id, appliedFilters, startAbortableRequest]);

    const rowsForViz = React.useMemo<RidershipStopAggRecord[]>(() => projectAggregateMetrics(ridRows, aggregate) as RidershipStopAggRecord[], [ridRows, aggregate]);
    const appliedDirectionId = appliedFilters?.directionId === ENTIRE_ROUTE_DIRECTION_ID
        ? ENTIRE_ROUTE_DIRECTION_ID
        : (appliedFilters?.directionId ?? '');
    const appliedDirectionInternal = React.useMemo(() => {
        const directionInternalIds = appliedFilters?.directionInternalIds || [];
        if (directionInternalIds.length === 1) {
            return directionInternalIds[0];
        }
        if (directionInternalIds.length > 1) {
            return undefined;
        }
        return appliedFilters?.directionInternalId || directionInternal || undefined;
    }, [appliedFilters?.directionInternalId, appliedFilters?.directionInternalIds, directionInternal]);
    const appliedDirectionInternalIds = React.useMemo(() => {
        const directionInternalIds = appliedFilters?.directionInternalIds || [];
        if (directionInternalIds.length > 0) {
            return directionInternalIds;
        }
        return appliedDirectionInternal ? [appliedDirectionInternal] : [];
    }, [appliedFilters?.directionInternalIds, appliedDirectionInternal]);

    const routeData = React.useMemo(() => {
        if (!appliedFilters) {
            return {
                board: [],
                alight: [],
                load: [],
                loadPins: [],
                alightPins: [],
                boardPins: [],
                labels: [],
                polylines: [],
                stopIds: [],
            } as any;
        }
        const built = buildVizFromGtfsAndRows(routeInfo, appliedDirectionId, appliedDirectionInternalIds, rowsForViz as any);
        return built as any;
    }, [appliedFilters, routeInfo, appliedDirectionId, appliedDirectionInternalIds, rowsForViz]);

    const totalStopsCount = React.useMemo(() => {
        return (routeData?.stopIds?.length ?? routeData?.loadPins?.length ?? 0) as number;
    }, [routeData]);

    const filteredRouteData = React.useMemo(() => {
        if (!selectedStopIds.length) {
            return routeData;
        }
        const allowed = new Set(selectedStopIds.map(id => String(id)));
        const filterPins = (pins?: PushpinProps[]) => (pins || []).filter(pin => {
            const stopId = (pin as any)?.options?.stopId;
            if (stopId === null || stopId === undefined) {
                return false;
            }
            return allowed.has(String(stopId));
        });
        const filteredStopIds: string[] = [];
        const filteredLabels: string[] = [];
        const filteredBoard: number[] = [];
        const filteredAlight: number[] = [];
        const filteredLoad: number[] = [];
        (routeData.stopIds || []).forEach((stopId: string, index: number) => {
            if (allowed.has(String(stopId))) {
                filteredStopIds.push(stopId);
                filteredLabels.push(routeData.labels[index]);
                filteredBoard.push(routeData.board[index]);
                filteredAlight.push(routeData.alight[index]);
                filteredLoad.push(routeData.load[index]);
            }
        });
        return {
            ...routeData,
            stopIds: filteredStopIds,
            labels: filteredLabels,
            board: filteredBoard,
            alight: filteredAlight,
            load: filteredLoad,
            loadPins: filterPins(routeData.loadPins),
            alightPins: filterPins(routeData.alightPins),
            boardPins: filterPins(routeData.boardPins),
        } as any;
    }, [routeData, selectedStopIds]);

    React.useEffect(() => {
        setSelectedFeaturesCount(0);
        setSelectedStopIds([]);
    }, [totalStopsCount, appliedFilters?.routeId, appliedFilters?.directionId, appliedFilters?.tripId]);

    React.useEffect(() => {
        if (!isHeatModeAvailable && mapMode === 'heat') {
            setMapMode('bubble');
        }
    }, [isHeatModeAvailable, mapMode]);

    React.useEffect(() => {
        if (mapMode !== 'heat') {
            return;
        }
        // Selection filtering is only available in Bubble mode.
        // Reset selection when switching to Heat mode to avoid hidden filters in charts/CSV.
        setSelectedFeaturesCount(0);
        setSelectedStopIds([]);
        setActiveBarIndex(null);
        setSelectionResetToken(prev => prev + 1);
    }, [mapMode]);

    React.useEffect(() => {
        setActiveBarIndex(null);
    }, [appliedFilters, selectedStopIds, aggregate]);

    const polylines = React.useMemo<PolylineProps[]>(() => {
        try {
            // Prefer GTFS shape-based polylines when available
            const anyRouteData: any = routeData as any;
            if (anyRouteData && anyRouteData.polylines) return anyRouteData.polylines as PolylineProps[];
            const locations = (routeData.pins || []).map((p: { location: any; }) => p.location) as any;
            if (!locations || locations.length === 0) return [];
            return [{
                locations,
                options: { strokeColor: '#026db5', strokeThickness: 3 },
            } as PolylineProps];
        } catch {
            return [];
        }
    }, [routeData]);

    const stopChartLayout = React.useMemo(() => {
        return buildStopChartLayout(filteredRouteData?.labels ?? []);
    }, [filteredRouteData?.labels]);

    const stopChartRemountKey = `${filteredRouteData?.labels?.length ?? 0}-${appliedFilters?.routeId ?? ''}-${appliedFilters?.directionId ?? ''}`;

    const seatStats = React.useMemo(() => {
        const seatsArray = (stopSeatCapacityRows || [])
            .map(r => Number(r.seats))
            .filter(v => Number.isFinite(v) && v > 0);
        const singleSeats = seatsArray.length > 0
            ? seatsArray.reduce((a, b) => a + b, 0) / seatsArray.length
            : 0;

        const selectedDays = Math.max(1, moment(toDate).diff(moment(fromDate), 'days'));
        const seatMultiplier = aggregate === 'sum' ? selectedDays : 1;
        const flatSeatValue = singleSeats * seatMultiplier;
        return { flatSeatValue };
    }, [stopSeatCapacityRows, aggregate, fromDate, toDate]);

    const rowsForCsv = React.useMemo(() => {
        if (!rowsForViz) return [];
        if (!selectedStopIds.length) return rowsForViz;
        const allowed = new Set(selectedStopIds.map(id => String(id)));
        return rowsForViz.filter(r => allowed.has(String(r.stopId)));
    }, [rowsForViz, selectedStopIds]);

    const stopSeatCapacityCsvMap = React.useMemo(() => {
        const map = new Map<string, number>();
        const seatValue = seatStats.flatSeatValue;
        (rowsForCsv || []).forEach(row => {
            const stopKey = String(row.stopId ?? '');
            map.set(stopKey, seatValue);
        });
        return map;
    }, [rowsForCsv, seatStats.flatSeatValue]);

    const barData = React.useMemo(() => {
        const barsCount = filteredRouteData?.labels?.length ?? 0;
        const dsMap = {
            board: {
                label: 'Boardings',
                data: filteredRouteData.board,
                hidden: !layerVisibility.board,
                backgroundColor: buildBarColorSeries(
                    barsCount,
                    activeBarIndex,
                    barSeriesColors.board.activeBackground,
                    barSeriesColors.board.inactiveBackground,
                ),
                borderColor: buildBarColorSeries(
                    barsCount,
                    activeBarIndex,
                    barSeriesColors.board.activeBorder,
                    barSeriesColors.board.inactiveBorder,
                ),
                borderWidth: 1,
                categoryPercentage: 0.7,
                order: 1,
            },
            alight: {
                label: 'Alightings',
                data: filteredRouteData.alight,
                hidden: !layerVisibility.alight,
                backgroundColor: buildBarColorSeries(
                    barsCount,
                    activeBarIndex,
                    barSeriesColors.alight.activeBackground,
                    barSeriesColors.alight.inactiveBackground,
                ),
                borderColor: buildBarColorSeries(
                    barsCount,
                    activeBarIndex,
                    barSeriesColors.alight.activeBorder,
                    barSeriesColors.alight.inactiveBorder,
                ),
                borderWidth: 1,
                categoryPercentage: 0.5,
                order: 0,
            },
            load: {
                label: 'Load',
                data: filteredRouteData.load,
                hidden: !layerVisibility.load,
                backgroundColor: buildBarColorSeries(
                    barsCount,
                    activeBarIndex,
                    barSeriesColors.load.activeBackground,
                    barSeriesColors.load.inactiveBackground,
                ),
                borderColor: buildBarColorSeries(
                    barsCount,
                    activeBarIndex,
                    barSeriesColors.load.activeBorder,
                    barSeriesColors.load.inactiveBorder,
                ),
                borderWidth: 1,
                order: 2,
            },
        } as const;

        const order = ['board', 'alight', 'load'];
        const datasets: any[] = order.map(k => dsMap[k as 'board' | 'alight' | 'load']);

        const seatValues = (filteredRouteData.labels || []).map(() => seatStats.flatSeatValue);

        if (seatValues.some((v: number | null) => typeof v === 'number')) {
            datasets.push({
                type: 'line' as const,
                label: 'Seats',
                data: seatValues,
                borderColor: '#8e44ad',
                backgroundColor: 'rgba(142, 68, 173, 0.15)',
                borderWidth: 2,
                tension: 0.35,
                pointRadius: 3,
                pointHoverRadius: 4,
                pointBackgroundColor: '#8e44ad',
                pointBorderColor: '#8e44ad',
                yAxisID: 'y',
                order: 4,
                fill: false,
                spanGaps: true,
            });
        }

        return {
            labels: filteredRouteData.labels,
            datasets,
        } as any;
    }, [filteredRouteData, seatStats.flatSeatValue, activeBarIndex, layerVisibility]);

    const handleFromDateChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { value }: { value: string }) => {
        setFromDate(value);
    };    
    
    const handleToDateChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { value }: { value: string }) => {
        setToDate(value);
    };

    const timeRange1Change = (time: TimeRangeValueType) => {
        setTimeRangeState(time);
    };

    const handleApply = React.useCallback(() => {
        const directionRequired = !isDirectionOptional(selectedRoute);
        if (!props.agency?.id || !selectedRoute || (directionRequired && !selectedDirection)) {
            return;
        }
        const normalizedDayType = normalizeDayTypeFilter(dayType);
        const selectedDirectionId = selectedDirection?.id || ENTIRE_ROUTE_DIRECTION_ID;
        const isEntireRoute = selectedDirectionId === ENTIRE_ROUTE_DIRECTION_ID;
        const filters: AppliedFilters = {
            routeId: selectedRoute.id,
            directionId: selectedDirectionId,
            directionInternalId: isEntireRoute ? '' : (selectedDirection?.internalId || ''),
            directionInternalIds: isEntireRoute ? [] : (selectedDirection?.internalIds || []),
            fromDate,
            toDate,
            dayType: normalizedDayType,
            tripId: selectedTripFilters.tripId,
            tripIds: selectedTripFilters.tripIds || [],
            timeRange: timeRangeState,
        };
        setAppliedFilters(filters);
        setAppliedTimeRange(filters.timeRange);
        setHasApplied(true);
        setRidRows(null);
        setStopSeatCapacityRows([]);
        setActiveBarIndex(null);
        setAppliedRouteLabel(selectedRoute.displayName);
        setAppliedDirectionLabel(selectedDirection?.displayName || 'All directions');
    }, [props.agency?.id, selectedRoute, selectedDirection, fromDate, toDate, dayType, selectedTripFilters, timeRangeState]);

    const timeToMinutes = (time: string) => amPmToMinutes(time);
    const minutesToTime = (minutes: number) => minutesToAmPm(minutes);

    const minValue = timeToMinutes(timeRangeState.start);
    const maxValue = timeToMinutes(timeRangeState.end);
    const formatValue = React.useCallback((value?: number | null) => {
        if (typeof value !== 'number' || Number.isNaN(value)) {
            return '0';
        }
        return numberFormatter.format(Utils.roundNumber(value, 0));
    }, [numberFormatter]);

    React.useEffect(() => {
        setSelectedStopPopup(null);
    }, [appliedFilters?.routeId, appliedFilters?.directionId, appliedTimeRange.start, appliedTimeRange.end, appliedFilters?.fromDate, appliedFilters?.toDate]);

    const popupInfoboxes = React.useMemo(() => {
        if (!selectedStopPopup || typeof selectedStopPopup.lat !== 'number' || typeof selectedStopPopup.lon !== 'number') {
            return [];
        }
        const title = selectedStopPopup.stopName && selectedStopPopup.stopId
            ? `${selectedStopPopup.stopName} (${selectedStopPopup.stopId})`
            : (selectedStopPopup.stopName || selectedStopPopup.stopId || 'Stop');
        return [{
            location: [selectedStopPopup.lat, selectedStopPopup.lon] as [number, number],
            title,
            text: (
                <StopPopupContent
                    stopId={selectedStopPopup.stopId}
                    stopName={selectedStopPopup.stopName}
                    boarding={selectedStopPopup.boarding}
                    alighting={selectedStopPopup.alighting}
                    load={selectedStopPopup.load}
                    formatNumber={(v) => formatValue(v ?? 0)}
                />
            ),
            options: { showCloseButton: true, pixelOffset: [0, -15] },
        }];
    }, [selectedStopPopup, formatValue]);

    const chartSelectionDataRef = React.useRef<{
        stopIds: string[];
        loadPins: PushpinProps[];
        boardPins: PushpinProps[];
        alightPins: PushpinProps[];
    }>({
        stopIds: [],
        loadPins: [],
        boardPins: [],
        alightPins: [],
    });
    chartSelectionDataRef.current = {
        stopIds: (filteredRouteData.stopIds || []).map((id: unknown) => String(id ?? '')),
        loadPins: filteredRouteData.loadPins || [],
        boardPins: filteredRouteData.boardPins || [],
        alightPins: filteredRouteData.alightPins || [],
    };

    const handleMapStopClick = React.useCallback((info: {
        stopId?: string;
        boarding?: number;
        alighting?: number;
        load?: number;
        stopName?: string;
        lat?: number;
        lon?: number;
    }) => {
        setSelectedStopPopup({
            stopId: info.stopId,
            stopName: info.stopName,
            boarding: info.boarding,
            alighting: info.alighting,
            load: info.load,
            lat: info.lat,
            lon: info.lon,
        });

        const normalizedStopId = info.stopId === null || info.stopId === undefined
            ? ''
            : String(info.stopId).trim();

        const currentSelectionData = chartSelectionDataRef.current;
        let matchedIndex = findStopIndexByStopId(currentSelectionData.stopIds, normalizedStopId);
        if (matchedIndex < 0 && normalizedStopId) {
            const findInPins = (pins: PushpinProps[]) => {
                return (pins || []).findIndex(pin => String(pin?.options?.stopId ?? '').trim() === normalizedStopId);
            };
            matchedIndex = findInPins(currentSelectionData.loadPins);
            if (matchedIndex < 0) {
                matchedIndex = findInPins(currentSelectionData.boardPins);
            }
            if (matchedIndex < 0) {
                matchedIndex = findInPins(currentSelectionData.alightPins);
            }
        }

        setActiveBarIndex(matchedIndex >= 0 ? matchedIndex : null);
    }, []);

    const handleBarClick = React.useCallback((_event: ChartEvent, elements: ActiveElement[]) => {
        if (!elements?.length) {
            return;
        }

        const clickedElement = elements[0];
        if (clickedElement.datasetIndex < 0 || clickedElement.datasetIndex > 2) {
            return;
        }

        if (typeof clickedElement.index !== 'number') {
            return;
        }

        const nextActiveBarIndex = getNextActiveBarIndex(activeBarIndex, clickedElement.index);
        setActiveBarIndex(nextActiveBarIndex);

        if (nextActiveBarIndex === null) {
            setSelectedStopPopup(null);
            return;
        }

        const popupData = buildStopPopupFromBarSelection({
            stopId: filteredRouteData.stopIds?.[nextActiveBarIndex],
            stopLabel: filteredRouteData.labels?.[nextActiveBarIndex],
            boarding: filteredRouteData.board?.[nextActiveBarIndex],
            alighting: filteredRouteData.alight?.[nextActiveBarIndex],
            load: filteredRouteData.load?.[nextActiveBarIndex],
            loadPin: filteredRouteData.loadPins?.[nextActiveBarIndex],
            boardPin: filteredRouteData.boardPins?.[nextActiveBarIndex],
            alightPin: filteredRouteData.alightPins?.[nextActiveBarIndex],
        });
        setSelectedStopPopup({
            stopId: popupData.stopId,
            stopName: popupData.stopName,
            boarding: popupData.boarding,
            alighting: popupData.alighting,
            load: popupData.load,
            lat: popupData.lat,
            lon: popupData.lon,
        });
    }, [activeBarIndex, filteredRouteData]);

    const handleLegendClick = React.useCallback((event: ChartEvent, legendItem: LegendItem, legend: LegendElement<ChartType>) => {
        const defaultLegendHandler = ChartJs.defaults.plugins.legend.onClick;
        defaultLegendHandler.call(legend, event, legendItem, legend);

        const datasetIndex = legendItem.datasetIndex;
        const chart = legend.chart;
        if (typeof datasetIndex !== 'number' || !chart) {
            return;
        }
        const metricKey = getLayerVisibilityByDatasetIndex(datasetIndex);
        if (!metricKey) {
            return;
        }
        const isVisible = chart.isDatasetVisible(datasetIndex);
        setLayerVisibility((prevVisibility) => {
            if (prevVisibility[metricKey] === isVisible) {
                return prevVisibility;
            }
            return {
                ...prevVisibility,
                [metricKey]: isVisible,
            };
        });
    }, []);

    const chartOptions = React.useMemo<ChartOptions<'bar'>>(() => ({
        ...baseChartOptions,
        scales: {
            ...baseChartOptions.scales,
            x: {
                ...baseChartOptions.scales?.x,
                ticks: {
                    ...baseChartOptions.scales?.x?.ticks,
                    font: stopChartLayout.tickFont,
                },
            },
        },
        plugins: {
            ...baseChartOptions.plugins,
            legend: {
                ...baseChartOptions.plugins?.legend,
                onClick: handleLegendClick,
            },
        },
        onHover: (event, elements) => {
            const target = (event?.native as MouseEvent | undefined)?.target as HTMLCanvasElement | undefined;
            if (!target) {
                return;
            }
            const hasBarElement = !!elements?.some(element => element.datasetIndex >= 0 && element.datasetIndex <= 2);
            target.style.cursor = hasBarElement ? 'pointer' : 'default';
        },
        onClick: handleBarClick,
    }), [handleBarClick, handleLegendClick, stopChartLayout.tickFont]);

    return (
        <Segment basic style={{ display: 'flex', flexDirection: 'column' }}>
            <Header as="h1" className="reportHeader">Ridecheck/Auto Passenger Counter (APC) - Average Weekday Activity by Stop</Header>
            <Form>
                <Form.Group widths="equal">
                    <Form.Field>
                        <label className="inputLabel">Route</label>
                        <RouteSelect
                            items={routeOptions}
                            value={selectedRoute}
                            onChanged={setSelectedRoute}
                            loading={routesLoading}
                            disabled={routesLoading || routeOptions.length === 0}
                            clearable={false}
                            placeholder="Select route"
                        />
                    </Form.Field>
                    <Form.Field>
                        <label className="inputLabel">Direction</label>
                        <DirectionSelect
                            items={directionOptions}
                            value={selectedDirection}
                            onChanged={setSelectedDirection}
                            loading={directionsLoading}
                            disabled={directionsLoading || directionOptions.length === 0}
                            clearable={false}
                            placeholder="Direction"
                        />
                    </Form.Field>
                    <Form.Field>
                        <label className="inputLabel">Trip (optional)</label>
                        <TripSelect
                                  items={tripOptions}
                                  value={tripId || undefined}
                                  loading={tripsLoading}
                                  disabled={tripsLoading || tripOptions.length === 0}
                                  onChanged={(v) => setTripId(v || '')}
                                  placeholder="All trips"
                                  clearable
                                  />
                    </Form.Field>
                </Form.Group>
                <Form.Group widths="equal" inline className="inputGroup">
                    <Form.Field className="calendarField">
                        <label className="calendarInputLabel">Date from</label>
                        <DateInput className="bph-date"
                                   name="averageWeekdayFromDate"
                                   dateFormat="YYYY-MM-DD"
                                   placeholder="From date"
                                   value={fromDate}
                                   iconPosition="left"
                                   popupPosition="bottom center"
                                   closable={true}
                                   startMode="day"
                                   preserveViewMode={false}
                                   animation="fade"
                                   disableMinute={false}
                                   style={styles.date}
                                   onChange={handleFromDateChange}
                                   maxDate={new Date()}
                        />
                    </Form.Field>
                    <Form.Field className="calendarField">
                        <label className="calendarInputLabel">To date</label>
                        <DateInput className="bph-date"
                                   name="averageWeekdayToDate"
                                   dateFormat="YYYY-MM-DD"
                                   placeholder="To date"
                                   value={toDate}
                                   iconPosition="left"
                                   popupPosition="bottom center"
                                   closable={true}
                                   startMode="day"
                                   preserveViewMode={false}
                                   animation="fade"
                                   disableMinute={false}
                                   style={styles.date}
                                   onChange={handleToDateChange}
                                   maxDate={new Date()}
                        />
                    </Form.Field>
                    <Form.Field style={styles.time}>
                        <label className="inputLabel">Time Range: {timeRangeState.start} - {timeRangeState.end}</label>
                        <RangeSlider
                            containerSx={{ width: '300px', padding: '0px', margin: 'auto' }}
                            value={[minValue, maxValue]}
                            onChange={(_, newValue) => {
                                if (Array.isArray(newValue)) {
                                    timeRange1Change({
                                        start: minutesToTime(newValue[0]),
                                        end: minutesToTime(newValue[1]),
                                    });
                                }
                            }}
                            onChangeCommitted={(_, newValue) => {
                                if (Array.isArray(newValue)) {
                                    timeRange1Change({
                                        start: minutesToTime(newValue[0]),
                                        end: minutesToTime(newValue[1]),
                                    });
                                }
                            }}
                        />
                    </Form.Field>
                    <Form.Field className="calendarField">
                        <label className="inputLabel">Day Type</label>
                        <Dropdown className="ui dropdown" style={{ width: 120 }}
                                  selection fluid options={dayTypeOptions}
                                  value={dayType}
                                  onChange={(_, d) => setDayType(normalizeDayTypeFilter(d.value as any))}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label className="inputLabel">Aggregation</label>
                        <Dropdown
                            className="ui dropdown"
                            selection
                            fluid
                            options={[
                                { key: 'sum', value: 'sum', text: 'Sum' },
                                { key: 'avg', value: 'avg', text: 'Daily Average' },
                                { key: 'avg_trips', value: 'avg_trips', text: 'Average by Trip' },
                            ]}
                            value={aggregate}
                            onChange={(_, d) => setAggregate((d.value as RidershipAggregationMode) || 'sum')}
                        />
                    </Form.Field>
                    <Form.Field>
                        {reportLoading
                            ? (
                                <Button className="fieldControl"
                                        color="red"
                                        content="Cancel"
                                        onClick={handleCancel} />
                            )
                            : (
                                <Button className="primaryButton fieldControl"
                                        content="Apply"
                                        onClick={handleApply}
                                        disabled={routesLoading || directionsLoading || !selectedRoute || (!isDirectionOptional(selectedRoute) && !selectedDirection)} />
                            )}
                    </Form.Field>
                </Form.Group>
            </Form>

            <BlockUi blocking={ridershipLoading || vehicleCapacityLoading || stopSeatCapacityLoading} message="Loading ridership & capacity...">
            <Grid stackable columns={1}>
                <Grid.Row>
                    <Grid.Column>
                        <Segment>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8, gap: 8 }}>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                                    {isHeatModeAvailable
                                        ? (
                                            <>
                                                <Form size="small" style={{ margin: 0 }}>
                                                    <Form.Field style={{ margin: 0, minWidth: 180 }}>
                                                        <label className="inputLabel">Map mode</label>
                                                        <Dropdown
                                                            className="ui dropdown map-mode-dropdown"
                                                            selection
                                                            fluid
                                                            options={[
                                                                { key: 'bubble', value: 'bubble', text: 'Bubble' },
                                                                { key: 'heat', value: 'heat', text: 'Heat' },
                                                            ]}
                                                            value={mapMode}
                                                            onChange={(_, d) => setMapMode((d.value as RidershipMapMode) || 'bubble')}
                                                        />
                                                    </Form.Field>
                                                </Form>
                                                {mapMode === 'heat'
                                                    ? (
                                                        <Form size="small" style={{ margin: 0 }}>
                                                            <Form.Field style={{ margin: 0, minWidth: 210 }}>
                                                                <label className="inputLabel">Heat metric</label>
                                                                <Dropdown
                                                                    className="ui dropdown heat-metric-dropdown"
                                                                    selection
                                                                    fluid
                                                                    options={[
                                                                        { key: 'boarding', value: 'boarding', text: 'Boardings' },
                                                                        { key: 'alighting', value: 'alighting', text: 'Alightings' },
                                                                        { key: 'load', value: 'load', text: 'Load' },
                                                                    ]}
                                                                    value={heatMetric}
                                                                    onChange={(_, d) => setHeatMetric((d.value as RidershipHeatMetric) || 'load')}
                                                                />
                                                            </Form.Field>
                                                        </Form>
                                                    )
                                                    : null}
                                            </>
                                        )
                                        : <Label basic>Map mode: Bubble</Label>}
                                </div>
                                {isHeatModeAvailable && mapMode === 'heat'
                                    ? <Label basic color="orange">Experimental map mode</Label>
                                    : null}
                            </div>
                            <div style={{
                                width: '100%',
                                height: 400,
                            }}>
                                <AzureMapSelectionMap
                                    mapMode={mapMode}
                                    heatMetric={heatMetric}
                                    showBoardingsLayer={layerVisibility.board}
                                    showAlightingsLayer={layerVisibility.alight}
                                    showLoadLayer={layerVisibility.load}
                                    showZoomControls={true}
                                    loadBubblePins={filteredRouteData.loadPins}
                                    alightingsBubblePins={filteredRouteData.alightPins}
                                    boardBubblePins={filteredRouteData.boardPins}
                                    polylinesProp={polylines}
                                    infoboxesProp={popupInfoboxes}
                                    onStopClick={handleMapStopClick}
                                    onSelectionChanged={mapMode === 'bubble'
                                        ? (features) => {
                                            const ids = new Set<string>();
                                            (features || []).forEach((feature: any) => {
                                                const props = feature?.getProperties ? feature.getProperties() : feature?.properties || {};
                                                const stopId = props?.stopId;
                                                if (stopId !== null && stopId !== undefined) {
                                                    ids.add(String(stopId));
                                                }
                                            });
                                            const selectedIds = Array.from(ids);
                                            setSelectedStopIds(selectedIds);
                                            setSelectedFeaturesCount(selectedIds.length);
                                        }
                                        : undefined}
                                    selectionResetToken={selectionResetToken}
                                />
                            </div>
                            <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                                <RidershipMapLegend mapMode={isHeatModeAvailable ? mapMode : 'bubble'} heatMetric={heatMetric}/>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {mapMode === 'bubble'
                                        ? (
                                            <Button
                                                basic
                                                size="small"
                                                disabled={selectedStopIds.length === 0}
                                                content="Reset selection"
                                                onClick={() => {
                                                    setSelectedStopIds([]);
                                                    setSelectedFeaturesCount(0);
                                                    setSelectionResetToken(prev => prev + 1);
                                                }}
                                            />
                                        )
                                        : <Label basic size="small">Selection unavailable in Heat mode</Label>}
                                    <Label color="blue">Stops: {numberFormatter.format(totalStopsCount)}</Label>
                                    {mapMode === 'bubble'
                                        ? <Label color="purple">Selected: {numberFormatter.format(selectedFeaturesCount)}</Label>
                                        : null}
                                </div>
                            </div>
                        </Segment>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <RidershipExportButton
                                rows={rowsForCsv}
                                stopSeatCapacityMap={stopSeatCapacityCsvMap}
                                aggregateMode={aggregate}
                                isLoading={ridershipLoading || stopSeatCapacityLoading}
                                disabled={!appliedFilters}
                                context={{
                                    routeId: appliedFilters?.routeId,
                                    routeLabel: appliedRouteLabel || selectedRoute?.displayName,
                                    directionId: appliedFilters?.directionId,
                                    directionLabel: appliedDirectionLabel || selectedDirection?.displayName,
                                    tripId: appliedFilters?.tripId,
                                    fromDate: appliedFilters?.fromDate,
                                    toDate: appliedFilters?.toDate,
                                    dayType: appliedFilters?.dayType,
                                    timeRangeStart: appliedFilters?.timeRange.start,
                                    timeRangeEnd: appliedFilters?.timeRange.end,
                                }}
                                triggerButtonProps={{ basic: true, size: 'small' }}
                            />
                        </div>
                        <Segment style={{ overflow: 'hidden' }}>
                            <div style={{ height: stopChartLayout.height, overflow: 'hidden' }}>
                                <Bar key={stopChartRemountKey} data={barData} options={chartOptions} plugins={[ChartDataLabels]} />
                            </div>
                        </Segment>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            </BlockUi>
        </Segment>
    );
};

function mapStateToProps(state: AppState): Props {
    return {
        agency: getSelectedOrDefaultAgency(state),
    } as Props;
}

export default connect(mapStateToProps)(RidershipWeekdayByStopPage);
